'use client';

import React, { useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselControls,
  CarouselNavigation,
  CarouselDots,
  type CarouselApi,
} from '@/app/_components/core/carousel';
import { ComponentSection } from '@/app/_components/core/component-section';
import { SectionIntro } from '@/app/_components/core/section-intro';
import { getItemTrackId, getSectionProps, getTrackingPrefix } from '@/app/_components/shared/utils/component-helpers';
import { useLabel } from '@/app/_contexts/labels-context';
import { useResponsiveSlidesPerView } from '@/app/_hooks/use-responsive-slides-per-view';
import { cn } from '@/app/_lib/utils';
import { CardCarouselComponentProps } from './card-carousel.types';
import { CardItem } from './card-item';

const THEME_CLASS_MAP: Record<string, string> = {
  theme0: 'bg-background',
  theme1: 'bg-card-carousel-accent',
};

export default function CardCarouselComponent({
  component,
  componentPosition,
  isNested,
}: CardCarouselComponentProps) {
  const {
    intro,
    cardType,
    cardContent,
    displayMode = 'carousel',
    showNavigation = true,
    showDots = true,
    autoplay = false,
    theme,
  } = component;
  const isGrid = displayMode === 'grid';
  const trackingPrefix = getTrackingPrefix(component);
  const [api, setApi] = useState<CarouselApi>();
  const cards = cardContent;
  const currentSlidesPerView = useResponsiveSlidesPerView(3);
  const canScroll = cards.length > currentSlidesPerView;
  const carouselLabel = useLabel('carousel.cardGallery', 'Card gallery');
  const isImageCard = cardType === 'imageCard';
  const showPeek = cards.length > 1 && !isImageCard && !isGrid;
  // SSR-stable center logic via responsive Tailwind classes (no JS dependency on
  // useResponsiveSlidesPerView, which would cause a layout shift after hydration):
  // imageCard slides per view are 1 (mobile) / 2 (md) / 3 (lg+).
  const centerClass = isImageCard
    ? cn(
        cards.length === 1 && 'md:justify-center',
        cards.length === 2 && 'lg:justify-center'
      )
    : '';
  const isTheme1 = theme === 'theme1';
  const themeClass =
    THEME_CLASS_MAP[theme ?? 'theme0'] ?? THEME_CLASS_MAP.theme0;

  if (cards.length === 0) {
    return null;
  }

  return (
    <ComponentSection isNested={isNested}
      {...getSectionProps(component, componentPosition, isNested)}
      className={cn(
        'py-section-padding-y-mobile xl:py-section-padding-y-desktop',
        themeClass
      )}
    >
      <div
        className={cn(
          'container mx-auto flex flex-col',
          showPeek && 'pr-0 sm:pr-4 xl:pr-6'
        )}
      >
        {intro && (
          <div
            className={cn(
              showPeek && 'pr-4 sm:pr-0',
              isTheme1 && 'dark text-foreground'
            )}
          >
            <SectionIntro
              intro={intro}
              withMoreLink
              trackingPrefix={trackingPrefix}
            />
          </div>
        )}

        {isGrid ? (
          // Same flex track model as the carousel (basis + pl gutter + negative
          // container margin), but flex-wrap so rows break instead of scroll.
          // centerClass reuses the carousel's centering (1 card md+, 2 cards lg+).
          <div
            className={cn(
              'ml-card-carousel-ml-mobile flex flex-wrap xl:ml-card-carousel-ml-desktop',
              centerClass
            )}
          >
            {cards.map((card, index) => (
              <div
                key={card.id || index}
                className={cn(
                  'pb-card-carousel-pl-mobile pl-card-carousel-pl-mobile xl:pb-card-carousel-pl-desktop xl:pl-card-carousel-pl-desktop',
                  isImageCard
                    ? 'basis-full md:basis-1/2 lg:basis-1/3'
                    : 'basis-(--card-carousel-basis-mobile) md:basis-(--card-carousel-basis-tablet) lg:basis-(--card-carousel-basis-desktop)'
                )}
              >
                <div className="h-full">
                  <CardItem
                    card={card}
                    cardType={cardType}
                    priority={componentPosition === 0 && index === 0}
                    trackId={
                      trackingPrefix
                        ? `${trackingPrefix}-card-${getItemTrackId(card.id, index)}`
                        : undefined
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Carousel
            setApi={setApi}
            opts={{
              align: 'start',
              loop: autoplay && canScroll,
              watchDrag: canScroll,
            }}
            autoplay={autoplay && canScroll}
            ariaLabel={carouselLabel}
            className="w-full"
          >
            <CarouselContent
              className={cn(
                'ml-card-carousel-ml-mobile pb-8 xl:ml-card-carousel-ml-desktop',
                centerClass
              )}
            >
              {cards.map((card, index) => (
                <CarouselItem
                  key={card.id || index}
                  className={cn(
                    'pl-card-carousel-pl-mobile xl:pl-card-carousel-pl-desktop',
                    isImageCard
                      ? 'basis-full md:basis-1/2 lg:basis-1/3'
                      : 'basis-(--card-carousel-basis-mobile) md:basis-(--card-carousel-basis-tablet) lg:basis-(--card-carousel-basis-desktop)'
                  )}
                >
                  <div className="h-full">
                    <CardItem
                      card={card}
                      cardType={cardType}
                      priority={componentPosition === 0 && index === 0}
                      trackId={
                        trackingPrefix
                          ? `${trackingPrefix}-card-${getItemTrackId(card.id, index)}`
                          : undefined
                      }
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            {(showNavigation || showDots) && canScroll && (
              <CarouselControls className="mt-2 lg:mt-4">
                {showNavigation && <CarouselNavigation api={api} />}
                {showDots && <CarouselDots api={api} />}
              </CarouselControls>
            )}
          </Carousel>
        )}
      </div>
    </ComponentSection>
  );
}
